import React, { lazy } from 'react';
import { IRiftRoute } from 'rift-router';
import { VIconsType } from '../components/commons/Icons/IconsType';
import NotFound404 from '../components/templates/NotFound404/NotFound404';
import authService from '../services/AuthServices';
import { retryImport } from '../utils/RetryImport';
import appStore from './AppStore';
import { getPermission } from '../../modules/commons/utils/utils';
import NewPortalLayout from '../../modules/newportal/containers/NewPortalLayout';

const requireAuth = (): any => {
  if (!authService.isLoggedIn() && process.env.REACT_APP_DS !== 'DEPLOY') return true;
};

/**
 *
 * @param permission
 * @returns true for hide tab
 */
const hideTab = (permission: string) => {
  const permissions: any = appStore.getUserGroupsFromLocalStorageByKey(permission);
  return appStore.isNewPortalCreation
    ? !(permission === 'tabNewPortal')
    : permission === 'tabNewPortal'
    ? true
    : getPermission(permissions);
};

const activateTab = (permission: string, initTab?: string) => {
  if (requireAuth() || (permission !== 'noTab' && hideTab(permission))) return '/login';
  appStore.setActiveTabItem(!!initTab ? initTab : permission);
};

const Main = lazy(() => retryImport(() => import('../components/templates/Main')));

/**
 * @description Imports for Setup menu
 */
const SetupLayout = lazy(() => retryImport(() => import('../../modules/setup')));
const LocationLayout = lazy(() => retryImport(() => import('../../modules/setup/location/containers/LocationLayout')));
const PickUpLayout = lazy(() =>
  retryImport(() => import('../../modules/setup/location/pickUp/containers/PickUpLayout'))
);
const DropOffLayout = lazy(() =>
  retryImport(() => import('../../modules/setup/location/dropOff/containers/DropOffLayout'))
);
const ContractLayout = lazy(() =>
  retryImport(() => import('../../modules/setup/location/contract/containers/ContractLayout'))
);
const ImportLayout = lazy(() =>
  retryImport(() => import('../../modules/setup/location/import/containers/ImportLayout'))
);
const OperatorLayout = lazy(() =>
  retryImport(() => import('../../modules/setup/location/operator/containers/OperatorLayout'))
);
const TerminalLayout = lazy(() =>
  retryImport(() => import('../../modules/setup/location/terminal/containers/TerminalLayout'))
);
const DriverContractorLayout = lazy(() =>
  retryImport(() => import('../../modules/setup/driverContractor/containers/DriverContractorLayout'))
);
const DriverLayout = lazy(() =>
  retryImport(() => import('../../modules/setup/driverContractor/driver/containers/DriverLayout'))
);
const ContractorLayout = lazy(() =>
  retryImport(() => import('../../modules/setup/driverContractor/contractor/containers/ContractorLayout'))
);
const StationLogDriverLayout = lazy(() =>
  retryImport(() => import('../../modules/setup/driverContractor/stationLogDriver/containers/StationLogDriverLayout'))
);
const TruckTrailer = lazy(() => retryImport(() => import('../../modules/setup/truckTrailer/index')));
const AccountLayout = lazy(() => retryImport(() => import('../../modules/setup/account/index')));
const HierarchyLayout = lazy(() => retryImport(() => import('../../modules/setup/hierarchy/index')));
const AdministrationLayout = lazy(() => retryImport(() => import('../../modules/setup/administration')));
const UsersLayout = lazy(() => retryImport(() => import('../../modules/setup/administration/users')));
const UserGroupsLayout = lazy(() => retryImport(() => import('../../modules/setup/administration/userGroups')));
const CompanyLayout = lazy(() => retryImport(() => import('../../modules/setup/administration/company')));
const DocumentsLayout = lazy(() => retryImport(() => import('../../modules/setup/administration/documents')));
const ApiSettingsLayout = lazy(() =>
  retryImport(() => import('../../modules/setup/administration/apiSettings/containers/ApiSettingsLayout'))
);
const DefinitionsLayout = lazy(() => retryImport(() => import('../../modules/setup/definitions')));
const CodeLayout = lazy(() => retryImport(() => import('../../modules/setup/definitions/code')));
const ReasonCodeLayout = lazy(() => retryImport(() => import('../../modules/setup/definitions/reasonCode')));
const FormsAndFieldsLayout = lazy(() => retryImport(() => import('../../modules/setup/definitions/formsAndFields')));
const CommoditiesLayout = lazy(() => retryImport(() => import('../../modules/setup/definitions/commodities')));
const RTUFormsAndFieldsLayout = lazy(() =>
  retryImport(() => import('../../modules/setup/definitions/rtuFormsAndFields'))
);

/**
 * @description Imports for Load menu
 */
const LoadLayout = lazy(() => retryImport(() => import('../../modules/load')));
const ManageLoadLayout = lazy(() =>
  retryImport(() => import('../../modules/load/components/manageLoads/containers/ManageLoadsLayout'))
);

const ManageScheduleLoadLayout = lazy(() =>
  retryImport(() => import('../../modules/load/components/manageScheduleLoads/containers/ManageScheduleLoadsLayout'))
);

const OpenLoadsLayout = lazy(() =>
  retryImport(() => import('../../modules/load/components/manageLoads/components/openLoads/containers/OpenLoadsLayout'))
);

const PendingLoadsLayout = lazy(() =>
  retryImport(
    () => import('../../modules/load/components/manageLoads/components/pendingLoads/containers/PendingLoadsLayout')
  )
);

const MetricsLoadsLayout = lazy(() =>
  retryImport(
    () => import('../../modules/load/components/manageLoads/components/metricsLoads/containers/MetricsLoadsLayout')
  )
);
const ImportLoadLayout = lazy(() =>
  retryImport(() => import('../../modules/load/components/importLoads/containers/ImportLoadsLayout'))
);
const ManageScenariosLayout = lazy(() =>
  retryImport(() => import('../../modules/load/components/manageScenarios/containers/ManageScenariosLayout'))
);
const MainScenariosLayout = lazy(() =>
  retryImport(
    () => import('../../modules/load/components/manageScenarios/components/mainScenario/containers/MainScenariosLayout')
  )
);
const SecondaryScenariosLayout = lazy(() =>
  retryImport(
    () =>
      import(
        '../../modules/load/components/manageScenarios/components/secondaryScenarios/containers/SecondaryScenariosLayout'
      )
  )
);
const ImportScenariosLayout = lazy(() =>
  retryImport(() => import('../../modules/load/components/importScenarios/containers/ImportScenariosLayout'))
);

/**
 * @description Imports for Dispatch menu
 */
const DispatchLayout = lazy(() => retryImport(() => import('../../modules/dispatch')));
const DispatchLoadLayout = lazy(() =>
  retryImport(() => import('../../modules/dispatch/components/dispatchLoad/containers/DispatchLoadLayout'))
);
const TrackingLayout = lazy(() =>
  retryImport(() => import('../../modules/dispatch/components/tracking/containers/TrackingLayout'))
);
const DriverScheduleContainer = lazy(() =>
  retryImport(() => import('../../modules/dispatch/components/driverSchedule/container/DriverScheduleContainer'))
);
const DriverDetailsLayout = lazy(() =>
  retryImport(() => import('../../modules/dispatch/components/driverDetails/containers/DriverDetailsLayout'))
);
const DriverShiftEmailLayout = lazy(() =>
  retryImport(() => import('../../modules/dispatch/components/driverShiftEmail/containers/DriverShiftEmailLayout'))
);

/**
 * @description Imports for Review menu
 */
const ReviewLayout = lazy(() => retryImport(() => import('../../modules/review/containers/ReviewLayout')));
const ReviewTabLayout = lazy(() =>
  retryImport(() => import('../../modules/review/components/reviewTab/containers/ReviewTabLayout'))
);
const CustomReportsLayout = lazy(() =>
  retryImport(() => import('../../modules/review/components/customReports/containers/CustomReportsLayout'))
);
const ComplianceReviewLayout = lazy(() =>
  retryImport(() => import('../../modules/review/compliance/containers/ComplianceReviewLayout'))
);
const FuelPurchaseLayout = lazy(() =>
  retryImport(() => import('../../modules/review/components/fuelPurchaseTab/containers/FuelPurchaseLayout'))
);
const VehicleMaintenanceLayout = lazy(() =>
  retryImport(() => import('../../modules/review/components/vehicleMaintenanceTab/containers/VehicleMaintenanceLayout'))
);
const DvirLayout = lazy(() => retryImport(() => import('../../modules/review/components/dvir/containers/DvirLayout')));
const DriverAddedTanksLayout = lazy(() =>
  retryImport(() => import('../../modules/review/components/driverAddedTanks/containers/DriverAddedTanksLayout'))
);
const TicketImportLayout = lazy(() =>
  retryImport(() => import('../../modules/review/components/ticketImport/containers/TicketImportLayout'))
);
const RtuLayout = lazy(() => retryImport(() => import('../../modules/review/components/rtu/containers/RtuLayout')));
const StationLogsLayout = lazy(() =>
  retryImport(() => import('../../modules/review/components/stationLogs/container/StationLogsLayout'))
);
const StationLogsTicketLayout = lazy(() =>
  retryImport(() => import('../../modules/review/components/stationLogs/components/stationLogsTicket'))
);
const StationLogsOpenCloseLayout = lazy(() =>
  retryImport(() => import('../../modules/review/components/stationLogs/components/stationLogsOpenClose'))
);

/**
 * @description Imports for Finance menu
 */
const FinanceLayout = React.lazy(() => retryImport(() => import('../../modules/finance')));
const RateSheetLayout = React.lazy(() =>
  retryImport(() => import('../../modules/finance/components/RateSheet/containers/RateSheetLayout'))
);
const InvoiceLayout = React.lazy(() =>
  retryImport(() => import('../../modules/finance/components/Invoice/containers/InvoiceLayout'))
);

const PayrollLayout = React.lazy(() =>
  retryImport(() => import('../../modules/finance/components/Payroll/containers/PayrollLayout'))
);

const FuelSurchargeLayout = React.lazy(() =>
  retryImport(() => import('../../modules/finance/components/FuelSurcharge/containers/FuelSurchargeLayout'))
);

const MessagingLayout = lazy(() => retryImport(() => import('../../modules/dispatch/components/messaging')));
const LoginContainer = lazy(() => retryImport(() => import('../../modules/security/user')));
const GeoTabLayout = lazy(() => retryImport(() => import('../../modules/geotab')));
const ZohoDashboard = React.lazy(() => retryImport(() => import('../../modules/dashboard')));

/**
 * isRoot: for elements without parents
 * label: label from Int file, example: setup.locations.locations
 * iconName: icon to show in menu
 * tabName: name of tab
 * haveChildren: if have a children
 * completePath: complete tab, include the parent path
 */
export interface IAppRoute extends IRiftRoute {
  isRoot?: boolean;
  label?: string;
  iconName?: VIconsType;
  tabName?: string;
  children?: IAppRoute[];
  completePath?: string;
  permission?: string;
  parent?: string;
}

const routeStore: IAppRoute[] = [
  {
    component: () => <LoginContainer />,
    path: '/login'
  },
  {
    component: () => '',
    onEnter: () => '/dashboard',
    path: '/'
  },
  {
    component: () => <NotFound404 />,
    path: '*'
  },
  {
    component: () => <Main />,
    path: '',
    children: [
      {
        component: () => <ZohoDashboard />,
        onEnter: () => activateTab('noTab', 'tabDashboard'),
        path: '/dashboard',
        iconName: 'home',
        label: 'dashboard.dashboard',
        isRoot: true,
        permission: 'tabDashboard'
      },
      {
        component: () => <NewPortalLayout />,
        onEnter: () => activateTab('tabNewPortal'),
        path: '/newportal',
        iconName: 'home',
        label: 'newPortal.newPortal',
        isRoot: true,
        permission: 'tabNewPortal'
      },
      {
        component: () => <SetupLayout />,
        path: '/setup',
        label: 'setup.setup',
        iconName: 'setup_alt',
        permission: 'tabSetup',
        isRoot: true,
        children: [
          {
            component: () => <LocationLayout />,
            path: '/locations',
            label: 'setup.locations.locations',
            iconName: 'map_pin',
            permission: 'tabSetupLocation',
            children: [
              {
                component: () => <PickUpLayout />,
                onEnter: () => activateTab('tabSetupLocationPickUp'),
                path: '/pickup',
                label: 'setup.locations.pickUp.pickup',
                completePath: '/setup/locations/pickup',
                iconName: 'setup_pickup_dropoff',
                permission: 'tabSetupLocationPickUp'
              },
              {
                component: () => <DropOffLayout />,
                onEnter: () => activateTab('tabSetupLocationDropOff'),
                path: '/dropoff',
                label: 'setup.locations.dropOff.dropoff',
                completePath: '/setup/locations/dropoff',
                iconName: 'setup_pickup_dropoff_alt',
                permission: 'tabSetupLocationDropOff'
              },
              {
                component: () => <ContractLayout />,
                onEnter: () => activateTab('tabSetupLocationContract'),
                path: '/contract',
                label: 'setup.locations.contract.contract',
                completePath: '/setup/locations/contract',
                iconName: 'setup_pickup_dropoff',
                permission: 'tabSetupLocationContract'
              },
              {
                component: () => <ImportLayout />,
                onEnter: () => activateTab('tabSetupLocationImport'),
                path: '/import',
                label: 'setup.import.import',
                completePath: '/setup/locations/import',
                iconName: 'pickup',
                permission: 'tabSetupLocationImport'
              },
              {
                component: () => <OperatorLayout />,
                onEnter: () => activateTab('tabSetupLocationOperator'),
                path: '/operator',
                label: 'setup.locations.operator.operator',
                completePath: '/setup/locations/operator',
                iconName: 'support',
                permission: 'tabSetupLocationOperator'
              },
              {
                component: () => <TerminalLayout />,
                onEnter: () => activateTab('tabSetupLocationTerminal'),
                path: '/terminal',
                label: 'setup.locations.terminal.terminal',
                tabName: 'Terminal',
                completePath: '/setup/locations/terminal',
                iconName: 'tracking_pickup',
                permission: 'tabSetupLocationTerminal'
              }
            ]
          },
          {
            component: () => <DriverContractorLayout />,
            path: '/drivercontractor',
            label: 'setup.driverContractor.driverContractor',
            iconName: 'driver',
            permission: 'tabSetupDriverContractor',
            children: [
              {
                component: () => <DriverLayout />,
                onEnter: () => activateTab('tabSetupDriverContractorDriver'),
                path: '/driver',
                label: 'setup.driverContractor.driver.driver',
                completePath: '/setup/drivercontractor/driver',
                iconName: 'driver',
                permission: 'tabSetupDriverContractorDriver'
              },
              {
                component: () => <ContractorLayout />,
                onEnter: () => activateTab('tabSetupDriverContractorContractor'),
                path: '/contractor',
                label: 'setup.driverContractor.contractor.contractor',
                completePath: '/setup/drivercontractor/contractor',
                iconName: 'setup_contractor',
                permission: 'tabSetupDriverContractorContractor'
              },
              {
                component: () => <StationLogDriverLayout />,
                onEnter: () => activateTab('tabSetupDriverContractorStationLogDriver'),
                path: '/stationlogdriver',
                label: 'setup.driverContractor.stationlogdriver.stationlogdriver',
                completePath: '/setup/drivercontractor/stationlogdriver',
                iconName: 'setup_driver',
                permission: 'tabSetupDriverContractorStationLogDriver'
              }
            ]
          },
          {
            component: () => <TruckTrailer />,
            onEnter: () => activateTab('tabSetupTruckTrailer'),
            path: '/trucktrailer',
            label: 'setup.truckTrailer.truckTrailer',
            iconName: 'truck',
            permission: 'tabSetupTruckTrailer',
            completePath: '/setup/trucktrailer'
          },
          {
            component: () => <AccountLayout />,
            onEnter: () => activateTab('tabSetupAccount'),
            path: '/account',
            label: 'setup.account.account',
            iconName: 'setup_account_alt',
            permission: 'tabSetupAccount',
            completePath: '/setup/account'
          },
          {
            component: () => <HierarchyLayout />,
            onEnter: () => activateTab('tabSetupHierarchy'),
            path: '/hierarchy',
            label: 'setup.hierarchy.hierarchy',
            iconName: 'hierarchy',
            permission: 'tabSetupHierarchy',
            completePath: '/setup/hierarchy'
          },
          {
            component: () => <AdministrationLayout />,
            path: '/administration',
            label: 'setup.administration.administration',
            iconName: 'setup_admin_alt',
            permission: 'tabSetupAdministration',
            children: [
              {
                component: () => <UsersLayout />,
                onEnter: () => activateTab('tabSetupAdministrationUser'),
                path: '/users',
                label: 'setup.user.user',
                completePath: '/setup/administration/users',
                iconName: 'user',
                permission: 'tabSetupAdministrationUser'
              },
              {
                component: () => <UserGroupsLayout />,
                onEnter: () => activateTab('tabSetupAdministrationUserGroup'),
                path: '/usersgroups',
                label: 'setup.userGroup.title',
                completePath: '/setup/administration/usersgroups',
                iconName: 'setup_admin_users',
                permission: 'tabSetupAdministrationUserGroup'
              },
              {
                component: () => <CompanyLayout />,
                onEnter: () => activateTab('tabSetupAdministrationCompany'),
                path: '/company',
                label: 'setup.company.company',
                completePath: '/setup/administration/company',
                iconName: 'setup_admin_company',
                permission: 'tabSetupAdministrationCompany'
              },
              {
                component: () => <DocumentsLayout />,
                onEnter: () => activateTab('tabSetupAdministrationDocuments'),
                path: '/documents',
                label: 'setup.documents.documents.title',
                completePath: '/setup/administration/documents',
                iconName: 'setup_admin_docs',
                permission: 'tabSetupAdministrationDocuments'
              },
              {
                component: () => <ApiSettingsLayout />,
                onEnter: () => activateTab('tabSetupAdministrationApiSettings'),
                path: '/apisettings',
                label: 'ApiSettings.apiSettings',
                completePath: '/setup/administration/apisettings',
                iconName: 'setup',
                permission: 'tabSetupAdministrationApiSettings'
              }
            ]
          },
          {
            component: () => <DefinitionsLayout />,
            path: '/definitions',
            label: 'setup.definitions.definitions',
            iconName: 'definition',
            permission: 'tabSetupDefinition',
            children: [
              {
                component: () => <CodeLayout />,
                onEnter: () => activateTab('tabSetupDefinitionCode'),
                path: '/code',
                label: 'setup.definitions.code.code',
                completePath: '/setup/definitions/code',
                iconName: 'setup_code',
                permission: 'tabSetupDefinitionCode'
              },
              {
                component: () => <ReasonCodeLayout />,
                onEnter: () => activateTab('tabSetupDefinitionReasonCode'),
                path: '/reasoncode',
                label: 'setup.definitions.reasonCode.title',
                completePath: '/setup/definitions/reasoncode',
                iconName: 'setup_code',
                permission: 'tabSetupDefinitionReasonCode'
              },
              {
                component: () => <FormsAndFieldsLayout />,
                onEnter: () => activateTab('tabSetupDefinitionDynamicLayout'),
                path: '/formsandfields',
                label: 'setup.definitions.formAndField.formAndFields',
                completePath: '/setup/definitions/formsandfields',
                iconName: 'form',
                permission: 'tabSetupDefinitionDynamicLayout'
              },
              {
                component: () => <CommoditiesLayout />,
                onEnter: () => activateTab('tabSetupDefinitionDynamicCommodities'),
                path: '/commodities',
                label: 'setup.definitions.commodities.title',
                completePath: '/setup/definitions/commodities',
                iconName: 'commodity',
                permission: 'tabSetupDefinitionDynamicCommodities'
              },
              {
                component: () => <RTUFormsAndFieldsLayout />,
                onEnter: () => activateTab('tabSetupDefinitionDynamicFormLayout'),
                path: '/rtuformandfields',
                label: 'setup.definitions.rtu.rtu',
                completePath: '/setup/definitions/rtuformandfields',
                iconName: 'form',
                permission: 'tabSetupDefinitionDynamicFormLayout'
              }
            ]
          }
        ]
      },
      {
        component: () => <LoadLayout />,
        path: '/load',
        label: 'load.load',
        iconName: 'load_manage_load',
        permission: 'tabLoad',
        isRoot: true,
        children: [
          {
            component: () => <ManageLoadLayout />,
            path: '/manageloads',
            label: 'load.manageLoads',
            iconName: 'load_manage_load_alt',
            permission: 'tabLoadManageLoad',
            children: [
              {
                component: () => <OpenLoadsLayout />,
                onEnter: () => activateTab('tabOpenLoads'),
                path: '/open',
                label: 'load.openLoads',
                completePath: '/load/manageloads/open',
                iconName: 'load_manage_load_alt',
                permission: 'tabOpenLoads'
              },
              {
                component: () => <PendingLoadsLayout />,
                onEnter: () => activateTab('tabPendingLoads'),
                path: '/pending',
                label: 'load.pendingLoads',
                completePath: '/load/manageloads/pending',
                iconName: 'load_manage_load_alt',
                permission: 'tabPendingLoads'
              },
              {
                component: () => <MetricsLoadsLayout />,
                onEnter: () => activateTab('tabMetricsLoads'),
                path: '/metricsloads',
                label: 'load.manageLoad.dropOffMetrics',
                completePath: '/load/manageloads/metricsloads',
                iconName: 'load_manage_load_alt',
                permission: 'tabMetricsLoads'
              }
            ]
          },
          {
            component: () => <ImportLoadLayout />,
            onEnter: () => activateTab('tabLoadImportLoad'),
            path: '/importloads',
            label: 'load.importLoads',
            completePath: '/load/importloads',
            iconName: 'import_load_setup_ticket',
            permission: 'tabLoadImportLoad'
          },
          {
            component: () => <ManageScheduleLoadLayout />,
            onEnter: () => activateTab('tabLoadManageScheduleLoad'),
            path: '/managescheduleloads',
            label: 'load.managescheduleloads',
            completePath: '/load/managescheduleloads',
            iconName: 'load_manage_load_alt',
            permission: 'tabLoadManageScheduleLoad'
          },
          {
            component: () => <ManageScenariosLayout />,
            path: '/managescenerarios',
            label: 'load.manageScenarios',
            iconName: 'routing_load_manage_scenarios',
            permission: 'tabLoadManageScenario',
            children: [
              {
                component: () => <MainScenariosLayout />,
                onEnter: () => activateTab('tabLoadManageScenarioMain'),
                path: '/primaryScenarios',
                label: 'load.managedScenario.primaryScenarios',
                completePath: '/load/managescenerarios/primaryScenarios',
                iconName: 'routing_load_manage_scenarios',
                permission: 'tabLoadManageScenarioMain'
              },
              {
                component: () => <SecondaryScenariosLayout />,
                onEnter: () => activateTab('tabLoadSecondaryScenario'),
                path: '/secondaryScenarios',
                label: 'load.managedScenario.secondaryScenarios',
                completePath: '/load/managescenerarios/secondaryScenarios',
                iconName: 'routing_load_manage_scenarios',
                permission: 'tabLoadSecondaryScenario'
              }
            ]
          },
          {
            component: () => <ImportScenariosLayout />,
            onEnter: () => activateTab('tabLoadImportScenario'),
            path: '/importscenarios',
            label: 'load.importScenarios',
            completePath: '/load/importscenarios',
            iconName: 'routing_load_manage_scenarios_alt',
            permission: 'tabLoadImportScenario'
          }
        ]
      },
      {
        component: () => <DispatchLayout />,
        path: '/dispatch',
        label: 'dispatch.dispatchBoard.tabTitle',
        iconName: 'dispatch',
        permission: 'tabDispatch',
        isRoot: true,
        children: [
          {
            component: () => <DispatchLoadLayout />,
            onEnter: () => activateTab('tabDispatchBoard'),
            path: '/board',
            label: 'dispatch.dispatchBoard.board',
            completePath: '/dispatch/board',
            iconName: 'dispatch',
            permission: 'tabDispatchBoard'
          },
          {
            component: () => <TrackingLayout />,
            onEnter: () => activateTab('tabDispatchMap'),
            path: '/tracking',
            label: 'dispatch.dispatchBoard.tracking',
            completePath: '/dispatch/tracking',
            iconName: 'tracking',
            permission: 'tabDispatchMap'
          },
          {
            component: () => <DriverScheduleContainer />,
            onEnter: () => activateTab('tabDispatchBoardSchedulerPlanner'),
            path: '/driverschedule',
            label: 'DriverSchedule.schedule',
            iconName: 'schedule',
            permission: 'tabDispatchBoardSchedulerPlanner',
            completePath: '/dispatch/driverschedule'
          },
          {
            component: () => <DriverDetailsLayout />,
            onEnter: () => activateTab('tabDispatchDriverDetails'),
            path: '/driverdetails',
            label: 'dispatch.dispatchBoard.driverDetails',
            iconName: 'dispatch',
            permission: 'tabDispatchDriverDetails',
            completePath: '/dispatch/driverdetails'
          },
          {
            component: () => <DriverShiftEmailLayout />,
            onEnter: () => activateTab('tabReportEmail'),
            path: '/drivershiftemail',
            label: 'dispatch.driverShiftEmail.title',
            iconName: 'dispatch',
            permission: 'tabReportEmail',
            completePath: '/dispatch/drivershiftemail'
          },
          {
            component: () => <MessagingLayout />,
            path: '/messaging',
            completePath: '/dispatch/messaging',
            onEnter: () => activateTab('tabDispatchMessaging'),
            label: 'dispatch.messaging.menu',
            iconName: 'messaging',
            permission: 'tabDispatchMessaging'
          }
        ]
      },
      {
        component: () => <ReviewLayout />,
        path: '/review',
        onEnter: () => activateTab('tabReviewReconcile'),
        label: 'review.title',
        iconName: 'review',
        permission: 'tabReviewReconcile',
        isRoot: true,
        children: [
          {
            component: () => <ReviewTabLayout />,
            onEnter: () => activateTab('tabReviewReconcileLoad'),
            path: '/reviewtab',
            label: 'review.title',
            completePath: '/review/reviewtab',
            iconName: 'review',
            permission: 'tabReviewReconcileLoad'
          },
          {
            component: () => <CustomReportsLayout />,
            onEnter: () => activateTab('tabReportReport'),
            path: '/reports',
            label: 'reports.tabTitle',
            completePath: '/review/reports',
            iconName: 'reports',
            permission: 'tabReportReport'
          },
          {
            component: () => <ComplianceReviewLayout />,
            onEnter: () => activateTab('tabReviewReconcileCompliance'),
            path: '/reconcile',
            label: 'review.complianceReview.title',
            completePath: '/review/reconcile',
            iconName: 'driver',
            permission: 'tabReviewReconcileCompliance'
          },
          {
            component: () => <FuelPurchaseLayout />,
            onEnter: () => activateTab('tabReviewReconcileFuelPurchase'),
            path: '/fuel',
            label: 'review.fuelPurchase.title',
            completePath: '/review/fuel',
            iconName: 'propane_tank',
            permission: 'tabReviewReconcileFuelPurchase'
          },
          {
            component: () => <VehicleMaintenanceLayout />,
            onEnter: () => activateTab('tabReviewReconcileVehicleMaintenance'),
            path: '/maintenance',
            label: 'review.vehicleMaintenance.title',
            completePath: '/review/maintenance',
            iconName: 'truck',
            permission: 'tabReviewReconcileVehicleMaintenance'
          },
          {
            component: () => <DvirLayout />,
            onEnter: () => activateTab('tabReviewReconcileDriverVehicleInspectionReport'),
            path: '/dvir',
            label: 'review.dvir.title',
            completePath: '/review/dvir',
            iconName: 'propane_tank',
            permission: 'tabReviewReconcileDriverVehicleInspectionReport'
          },
          {
            component: () => <DriverAddedTanksLayout />,
            onEnter: () => activateTab('tabReviewReconcileDriverAddedTanks'),
            path: '/dat',
            label: 'review.driverAddedTanks.title',
            completePath: '/review/dat',
            iconName: 'propane_tank',
            permission: 'tabReviewReconcileDriverAddedTanks'
          },
          {
            component: () => <TicketImportLayout />,
            onEnter: () => activateTab('tabReviewReconcileTicketImport'),
            path: '/ticketimport',
            label: 'review.ticketImport.title',
            completePath: '/review/ticketimport',
            iconName: 'import_load_setup_ticket',
            permission: 'tabReviewReconcileTicketImport'
          },
          {
            component: () => <RtuLayout />,
            onEnter: () => activateTab('tabReviewReconcileRTURTU'),
            path: '/rtu',
            label: 'review.rtu.title',
            completePath: '/review/rtu',
            iconName: 'driver',
            permission: 'tabReviewReconcileRTURTU'
          },
          {
            component: () => <StationLogsLayout />,
            path: '/stationlogs',
            label: 'review.stationLogs.title',
            iconName: 'driver',
            permission: 'tabReviewReconcileStationLog',
            children: [
              {
                component: () => <StationLogsTicketLayout />,
                onEnter: () => activateTab('tabReviewReconcileStationLogTickets'),
                path: '/tickets',
                label: 'review.stationLogs.ticket.title',
                completePath: '/review/stationlogs/tickets',
                iconName: 'review',
                permission: 'tabReviewReconcileStationLogTickets'
              },
              {
                component: () => <StationLogsOpenCloseLayout />,
                onEnter: () => activateTab('tabReviewReconcileStationLogOpenClose'),
                path: '/openclose',
                label: 'review.stationLogs.openClose.title',
                completePath: '/review/stationlogs/openclose',
                iconName: 'review',
                permission: 'tabReviewReconcileStationLogOpenClose'
              }
            ]
          }
        ]
      },
      {
        component: () => <GeoTabLayout />,
        path: '/eldPortal',
        onEnter: () => activateTab('tabELD'),
        label: 'app.eld',
        iconName: 'setup_driver_geotab',
        permission: 'tabELD',
        isRoot: true
      },
      {
        component: () => <FinanceLayout />,
        path: '/finance',
        label: 'finance.menu',
        iconName: 'finance_invoice',
        permission: 'tabAccounting',
        isRoot: true,
        children: [
          {
            component: () => <RateSheetLayout />,
            onEnter: () => activateTab('tabAccountingTicketRating'),
            path: '/contract',
            label: 'setup.locations.contract.contract',
            completePath: '/finance/contract',
            iconName: 'finance_invoice',
            permission: 'tabAccountingTicketRating'
          },
          {
            component: () => <InvoiceLayout />,
            onEnter: () => activateTab('tabAccountingInvoice'),
            path: '/invoice',
            label: 'finance.invoice.title',
            completePath: '/finance/invoice',
            iconName: 'finance_invoice',
            permission: 'tabAccountingInvoice'
          },
          {
            component: () => <PayrollLayout />,
            onEnter: () => activateTab('tabAccountingPayroll'),
            path: '/payroll',
            label: 'finance.payroll.title',
            completePath: '/finance/payroll',
            iconName: 'finance_payroll',
            permission: 'tabAccountingPayroll'
          },
          {
            component: () => <FuelSurchargeLayout />,
            onEnter: () => activateTab('tabAccountingFuelSurcharge'),
            path: '/fuelSurcharge',
            label: 'finance.fuelSurcharge.title',
            completePath: '/finance/fuelSurcharge',
            iconName: 'finance_payroll',
            permission: 'tabAccountingFuelSurcharge'
          }
        ]
      }
    ]
  }
];

export default routeStore;
